import { create } from 'zustand';

export type AgentProgress =
  | { kind: 'screen'; label: string }
  | { kind: 'recall'; facts: number; docs: number }
  | { kind: 'plan'; reads: number }
  | { kind: 'read'; resource: string; rowCount: number; truncated: boolean }
  | { kind: 'read_failed'; resource: string }
  | { kind: 'assess'; anotherPass: boolean }
  | { kind: 'answer' };

const MAX_LOG = 5;

interface ProgressSnapshot {
  activity: string | null;
  log: { resource: string; detail: string; failed: boolean }[];
  answering: boolean;
}

const EMPTY: ProgressSnapshot = { activity: null, log: [], answering: false };

const useStore = create<ProgressSnapshot>(() => EMPTY);

function describe(event: AgentProgress): string | null {
  switch (event.kind) {
    case 'screen':
      return `Reading ${event.label}`;
    case 'recall':
      return 'Recalling what I know';
    case 'plan':
      return event.reads > 0 ? 'Looking up your data' : 'Working from this screen';
    case 'read':
    case 'read_failed':
      return 'Looking up your data';
    case 'assess':
      return event.anotherPass ? 'Checking one more thing' : 'Putting it together';
    case 'answer':
      return null;
  }
}

function logEntryFor(event: AgentProgress): ProgressSnapshot['log'][number] | null {
  if (event.kind === 'read') {
    return {
      resource: event.resource,
      detail: `${event.rowCount} row${event.rowCount === 1 ? '' : 's'}${
        event.truncated ? ' (truncated)' : ''
      }`,
      failed: false,
    };
  }
  if (event.kind === 'read_failed') {
    return { resource: event.resource, detail: 'could not be read', failed: true };
  }
  return null;
}

export function applyProgress(event: AgentProgress): void {
  useStore.setState((state) => {
    // The answer event only flips the phase; it must not clear the read log,
    // which the panel keeps showing underneath the answer.
    if (event.kind === 'answer') return { activity: null, answering: true };

    const entry = logEntryFor(event);
    return {
      activity: describe(event) ?? state.activity,
      log: entry ? [...state.log, entry].slice(-MAX_LOG) : state.log,
    };
  });
}

export function resetProgress(): void {
  // Guarded because a merge-set always produces a fresh object and would notify
  // every subscriber; a turn that starts with nothing to clear should be silent.
  const { activity, log, answering } = useStore.getState();
  if (activity === null && log.length === 0 && !answering) return;
  useStore.setState(EMPTY, true);
}

/** Alias kept for tests that reset between cases; identical to resetProgress. */
export const resetProgressStore = resetProgress;

export function useAgentProgress(): ProgressSnapshot {
  return useStore();
}

export function progressState(): ProgressSnapshot {
  return useStore.getState();
}
